// Rules for VALIDATION_CONSTRAINTS.md section 9 — Activities.
//
// Scope notes:
//   - 9.1 (activity 1/1 cardinality) is already covered by rule 3.3; not
//     re-implemented here to avoid duplicate issues.
//   - 9.2 validates boundary attachment within the same process.
//   - 9.3/9.4/9.5 deal with activity-level scripting. The editor does not yet
//     persist a Diagram.Resources dict (see pssHelper.ts generateWorkflowProject),
//     so any InputScripts/OutputScripts/ExportScripts references on node.data
//     are currently non-functional and are surfaced as warnings rather than
//     hard errors.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import {
  buildLookup,
  getFamily,
  isActivity,
  labelOf,
} from '../helpers';

// Rule 9.1 skipped — covered by section_3_cardinality.ts rule 3.3.

const rule_9_2_BoundaryAttachment: ValidationRule = {
  id: '9.2',
  description:
    'Boundary nodes attached to an activity (via parentNode) must exist in the same process and be of family BoundaryEvent',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const activity of process.nodes) {
      if (!isActivity(activity)) continue;
      // Find every node that claims this activity as its parentNode.
      for (const candidate of process.nodes) {
        if ((candidate as any).parentNode !== activity.id) continue;
        // Must actually exist (it does, since we iterated process.nodes).
        // Must be BoundaryEvent family.
        if (getFamily(candidate.type) !== 'BoundaryEvent') {
          issues.push({
            severity: 'error',
            rule: '9.2',
            message: `${labelOf(candidate)} is attached to ${labelOf(activity)} via parentNode but is not a BoundaryEvent.`,
            processId: process.id,
            nodeIds: [candidate.id, activity.id],
          });
        }
      }
    }

    // Also flag boundary nodes whose parentNode points to a non-existent node
    // or to a non-activity node in this process.
    for (const n of process.nodes) {
      if (getFamily(n.type) !== 'BoundaryEvent') continue;
      const parentId = (n as any).parentNode as string | undefined;
      if (!parentId) continue;
      const parent = lookup.nodesById.get(parentId);
      if (!parent) {
        issues.push({
          severity: 'error',
          rule: '9.2',
          message: `${labelOf(n)} references non-existent parent node '${parentId}'.`,
          processId: process.id,
          nodeIds: [n.id],
        });
        continue;
      }
      if (!isActivity(parent)) {
        issues.push({
          severity: 'error',
          rule: '9.2',
          message: `${labelOf(n)} is attached to ${labelOf(parent)} which is not an Activity.`,
          processId: process.id,
          nodeIds: [n.id, parent.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_9_2_BoundaryAttachment);

const rule_9_3_ScriptResourceReferences: ValidationRule = {
  id: '9.3',
  description:
    'InputScripts/OutputScripts/ExportScripts resource ids — warning until the editor persists a Diagram.Resources dict',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      const d = n.data as any;
      if (!d) continue;
      const hasAny =
        (Array.isArray(d.inputScripts) && d.inputScripts.length > 0) ||
        (Array.isArray(d.outputScripts) && d.outputScripts.length > 0) ||
        (Array.isArray(d.exportScripts) && d.exportScripts.length > 0);
      if (!hasAny) continue;
      issues.push({
        severity: 'warning',
        rule: '9.3',
        message: `${labelOf(n)}: Script resource referencing is not yet supported by the editor — IDs will not be persisted.`,
        processId: process.id,
        nodeIds: [n.id],
      });
    }
    return issues;
  },
};
registerRule(rule_9_3_ScriptResourceReferences);

const rule_9_4_ScriptTaskEmptyScript: ValidationRule = {
  id: '9.4',
  description: 'ScriptTask with empty/null script is usually a mistake',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'ScriptTask') continue;
      const script = (n.data as any)?.script;
      if (script === undefined || script === null || script === '') {
        issues.push({
          severity: 'warning',
          rule: '9.4',
          message: `${labelOf(n)} has no script — engine will short-circuit to OutputScriptStage.`,
          processId: process.id,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_9_4_ScriptTaskEmptyScript);

const rule_9_5_ImportAllWithInputScripts: ValidationRule = {
  id: '9.5',
  description:
    'ImportAllToActivityContext=true with non-empty InputScripts — scripts will not run in that stage',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      const d = n.data as any;
      if (!d) continue;
      const importAll = d.importAllToActivityContext === true;
      const inputScripts = d.inputScripts;
      const hasInputs = Array.isArray(inputScripts) && inputScripts.length > 0;
      if (importAll && hasInputs) {
        issues.push({
          severity: 'warning',
          rule: '9.5',
          message: `${labelOf(n)}: InputScripts will be ignored when ImportAllToActivityContext is true.`,
          processId: process.id,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_9_5_ImportAllWithInputScripts);
